import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts';

const COLORS = ['#4d4cac', '#4caf50', '#ff9800', '#f44336', '#2196f3', '#9c27b0'];

const ReportVisualization = ({ data, chartType, reportName }) => {
  if (!data || !data.data) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography>لا توجد بيانات للعرض</Typography>
      </Paper>
    );
  }

  const rows = data.data || [];
  const groupedData = data.grouped_data || [];

  // Prepare chart data
  const prepareChartData = () => {
    if (groupedData.length > 0) {
      return groupedData.map((group) => ({
        name: Object.values(group.group).join(' - ') || 'مجموعة',
        value: group.count,
        ...group.group,
      }));
    }
    return rows.slice(0, 10); // Limit to 10 for chart
  };

  const chartData = prepareChartData();

  const renderTable = () => {
    if (rows.length === 0) return null;

    const headers = Object.keys(rows[0]);

    return (
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {headers.map((header) => (
                <TableCell key={header} sx={{ fontWeight: 'bold' }}>
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, idx) => (
              <TableRow key={idx}>
                {headers.map((header) => (
                  <TableCell key={header}>{row[header] || '-'}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderChart = () => {
    if (chartType === 'table' || chartType === 'none') {
      return renderTable();
    }

    if (chartData.length === 0) {
      return <Typography>لا توجد بيانات للرسم</Typography>;
    }

    switch (chartType) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="value" fill="#4d4cac" />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="value" stroke="#4d4cac" />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) =>
                  `${name}: ${(percent * 100).toFixed(0)}%`
                }
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        );

      default:
        return renderTable();
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      {reportName && (
        <Typography variant="h6" gutterBottom>
          {reportName}
        </Typography>
      )}
      <Box sx={{ mt: 2 }}>
        {renderChart()}
      </Box>
      {data.count && (
        <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
          إجمالي السجلات: {data.count}
        </Typography>
      )}
    </Paper>
  );
};

export default ReportVisualization;
